/**
 * Servicio para manejar los productos
 * 
 * Este servicio carga los datos de productos desde el archivo JSON local
 * y proporciona métodos para acceder a la información de productos.
 */

import productsData from '../../../exceldb/products.json';

// Mapa de productos para búsqueda rápida por nombre
const productsMap = new Map();

// Inicializar el mapa de productos
const initProductsMap = () => {
  productsData.forEach(product => {
    productsMap.set(product.name.toLowerCase(), product);
  });
};

// Inicializar el mapa al cargar el servicio
initProductsMap();

/**
 * Obtiene todos los productos
 * @returns {Array} - Array con todos los productos
 */
export const getAllProducts = () => {
  return productsData;
};

/**
 * Busca un producto por su nombre exacto
 * @param {string} name - Nombre exacto del producto
 * @returns {Object|null} - Producto encontrado o null si no existe
 */
export const getProductByExactName = (name) => {
  if (!name) return null;
  return productsMap.get(name.toLowerCase()) || null;
};

/**
 * Busca un producto por nombre parcial (contiene)
 * @param {string} partialName - Parte del nombre del producto
 * @returns {Object|null} - Primer producto que coincide o null si no hay coincidencias
 */
export const getProductByPartialName = (partialName) => {
  if (!partialName) return null;
  
  const searchTerm = partialName.toLowerCase();
  
  // Buscar por coincidencia exacta primero
  const exactMatch = productsMap.get(searchTerm);
  if (exactMatch) return exactMatch;
  
  // Buscar por coincidencia parcial
  for (const [key, product] of productsMap.entries()) {
    if (key.includes(searchTerm) || searchTerm.includes(key)) {
      return product;
    }
  }
  
  return null;
};

/**
 * Enriquece un item de cotización con datos completos del producto
 * @param {Object} item - Item de cotización
 * @returns {Object} - Item enriquecido con datos del producto
 */
export const enrichQuotationItem = (item) => {
  if (!item) return item;
  
  // Crear una copia del item para no modificar el original
  const enrichedItem = { ...item };
  
  // Buscar el producto por nombre
  const product = getProductByPartialName(item.name);
  
  // Si encontramos el producto, añadir los datos faltantes
  if (product) {
    // Solo añadir propiedades que no existan en el item original
    if (!enrichedItem.countPerBottle && product.countPerBottle) {
      enrichedItem.countPerBottle = product.countPerBottle;
    }
    
    if (!enrichedItem.moq && product.moq) {
      enrichedItem.moq = product.moq;
    }
    
    // Convertir el formato de pricing si es necesario
    if (!enrichedItem.pricing && product.pricing) {
      enrichedItem.pricing = {
        price200: product.pricing["200"],
        price500: product.pricing["500"],
        price1000: product.pricing["1000"]
      };
    }
    
    // Añadir categoría si no existe
    if (!enrichedItem.categoryName && product.category) {
      enrichedItem.categoryName = product.category;
    }
    
    // Añadir formatos disponibles si no existen
    if (!enrichedItem.formats && product.formats) {
      enrichedItem.formats = product.formats;
    }
  }
  
  return enrichedItem;
};

export default {
  getAllProducts,
  getProductByExactName,
  getProductByPartialName,
  enrichQuotationItem
};
